/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: promptGeneratorChatbot.ts
 🗓️ Created: 2026-05-03
 🧩 Description:
    Utility for generating AI Chatbot system prompts.
    Adapted for text-based interactions (removes voice specifics).
────────────────────────────────────────────────────────────────*/

export interface ChatbotPromptFormData {
    persona_name?: string;
    role_description?: string;
    company_name?: string;
    products?: string[];
    key_points?: string;
    do_rules?: string[];
    dont_rules?: string[];
    greeting_message?: string;
    closing_message?: string;
    disclosure_message?: string;
    additional_instructions?: string;
    team_members?: Record<string, string>;
    allow_language_change?: boolean;
    forced_language?: string;
    personality?: string;
    tone_of_voice?: string;
    language_level?: string;
    response_length?: string;
    reduce_repetition?: boolean;
}

/**
 * Generate a structured system prompt for text-based chatbots
 */
export function generateChatbotPromptFromFormData(formData: ChatbotPromptFormData): string {
    const lines: string[] = [];

    // ═══════════════════════════════════════════════════════════════
    // SECTION 1: ROLE & OBJECTIVE
    // ═══════════════════════════════════════════════════════════════
    lines.push('# ROLE & OBJECTIVE\n');

    if (formData.persona_name) {
        lines.push(`You are ${formData.persona_name}, a text-based AI assistant.`);
    } else {
        lines.push(`You are a text-based AI assistant.`);
    }
    
    if (formData.role_description) {
        lines.push(`${formData.role_description}`);
    }
    if (formData.company_name) {
        lines.push(`You represent ${formData.company_name}.`);
    }
    lines.push('');

    // ═══════════════════════════════════════════════════════════════
    // SECTION 2: PERSONALITY & TONE
    // ═══════════════════════════════════════════════════════════════
    lines.push('# PERSONALITY & TONE\n');
    lines.push(`- Be helpful, friendly, and professional in your interactions`);
    lines.push(`- Do not use filler words like "um" or "uh" since this is a text-based chat`);

    if (formData.personality) {
        lines.push(`- **Personality**: You are ${formData.personality}. Adapt your demeanor to reflect this trait.`);
    }
    if (formData.tone_of_voice) {
        lines.push(`- **Tone**: Keep a ${formData.tone_of_voice} tone throughout the conversation.`);
    }
    if (formData.language_level) {
        lines.push(`- **Language Level**: Use a ${formData.language_level} language style.`);
    }
    if (formData.response_length) {
        if (formData.response_length === 'brief') {
            lines.push(`- **Response Length**: Keep responses brief and concise (1-2 sentences maximum).`);
        } else if (formData.response_length === 'detailed') {
            lines.push(`- **Response Length**: Provide detailed and comprehensive responses (3-4 sentences or more).`);
        } else {
            lines.push(`- **Response Length**: Provide standard, balanced responses (2-3 sentences). Format with bullet points when appropriate.`);
        }
    } else {
        lines.push(`- Keep responses concise and formatted for readability (use bullet points or short paragraphs when appropriate)`);
    }
    if (formData.reduce_repetition !== false) {
        lines.push(`- Use varied vocabulary and avoid repetitive phrasing or starting sentences the same way.`);
    }

    lines.push('');

    // ═══════════════════════════════════════════════════════════════
    // SECTION 3: CONTEXT (Products, Key Info, Team)
    // ═══════════════════════════════════════════════════════════════
    const hasContext = (formData.products && formData.products.length > 0) ||
        formData.key_points ||
        (formData.team_members && Object.keys(formData.team_members).length > 0);

    if (hasContext) {
        lines.push('# CONTEXT\n');

        if (formData.products && formData.products.length > 0) {
            lines.push('**Products/Services:**');
            formData.products.forEach(product => {
                lines.push(`- ${product}`);
            });
            lines.push('');
        }

        if (formData.key_points) {
            lines.push('**Key Information:**');
            lines.push(formData.key_points);
            lines.push('');
        }

        if (formData.team_members && Object.keys(formData.team_members).length > 0) {
            lines.push('**Team Members:**');
            Object.entries(formData.team_members).forEach(([name, role]) => {
                lines.push(`- ${name}: ${role}`);
            });
            lines.push('');
        }
    }

    // ═══════════════════════════════════════════════════════════════
    // SECTION 4: INSTRUCTIONS / RULES (DO and DON'T)
    // ═══════════════════════════════════════════════════════════════
    const hasRules = (formData.do_rules && formData.do_rules.length > 0) ||
        (formData.dont_rules && formData.dont_rules.length > 0) ||
        !formData.allow_language_change;

    if (hasRules) {
        lines.push('# INSTRUCTIONS\n');

        if (formData.do_rules && formData.do_rules.length > 0) {
            lines.push('**ALWAYS:**');
            formData.do_rules.forEach(rule => {
                lines.push(`- ${rule}`);
            });
            lines.push('');
        }

        if (formData.dont_rules && formData.dont_rules.length > 0) {
            lines.push('**NEVER:**');
            formData.dont_rules.forEach(rule => {
                lines.push(`- ${rule}`);
            });
            lines.push('');
        }

        // Language control rule
        if (!formData.allow_language_change && formData.forced_language) {
            lines.push('**CRITICAL LANGUAGE RULE:**');
            lines.push(`- ALWAYS respond in ${formData.forced_language}, regardless of what language the user uses`);
            lines.push('');
        }
    }

    // ═══════════════════════════════════════════════════════════════
    // SECTION 5: CONVERSATION FLOW
    // ═══════════════════════════════════════════════════════════════
    if (formData.greeting_message || formData.closing_message) {
        lines.push('# CONVERSATION FLOW\n');
        if (formData.greeting_message) {
            lines.push(`**Opening:** Start with: "${formData.greeting_message}". Adapt and translate this opening message naturally to the language in which the user initiates the conversation.`);
        }
        if (formData.closing_message) {
            lines.push(`**Closing:** End chats with: "${formData.closing_message}". Adapt and translate this closing message naturally to the language used during the conversation.`);
        }
        lines.push('');
    }

    // ═══════════════════════════════════════════════════════════════
    // SECTION 6: SAFETY & ESCALATION (AI Disclosure)
    // ═══════════════════════════════════════════════════════════════
    if (formData.disclosure_message) {
        lines.push('# SAFETY & ESCALATION\n');
        lines.push(`If asked whether you are an AI or human, respond honestly: "${formData.disclosure_message}"`);
        lines.push('');
    }

    // ═══════════════════════════════════════════════════════════════
    // ADDITIONAL INSTRUCTIONS (Custom user content)
    // ═══════════════════════════════════════════════════════════════
    if (formData.additional_instructions && formData.additional_instructions.trim()) {
        lines.push('# ADDITIONAL INSTRUCTIONS\n');
        lines.push(formData.additional_instructions);
        lines.push('');
    }

    return lines.join('\n').trim();
}

/**
 * Estimate token count (roughly 4 characters per token for English)
 */
export function estimateTokens(text: string): number {
    if (!text) return 0;
    return Math.ceil(text.length / 4);
}
